/**
 * URL values vs markup ids of the spare-parts pages — the one place that
 * translates between them.
 *
 * Public ids appear only in URLs; markup keeps the integer ids (scraper docs
 * `wave-0/W0-4-public-id-urls.md` §1.2). So a drawing tab's DOM id is
 * `datte-ipl-<kCategory>-p`, while the `?category=` value it writes is
 * `datte-ipl-<publicId>`, carried in the element's `data-public-id` (or, on the
 * product page's JSON island, the row's `urlId`).
 *
 * Reading a URL value back: match it EXACTLY against an element's URL value or its
 * DOM id (an old integer link served from a cache before the server-side 301
 * existed). Never a substring match on our own values — a public id may contain
 * `-p` or any other short fragment. Values WITHOUT our prefix (legacy anchors like
 * `CTL_PL265`, source foreign ids from the pre-go-live pages) keep their old
 * substring fallback at the call site, gated by {@link isOwnShape} (decision D5).
 */

/** The prefixes of the values the platform itself writes into URLs. */
const OWN_PREFIXES = ['datte-ipl-', 'datte-node-'] as const;

/** Whether `value` is one of the platform's own URL values (old integer or new public-id form). */
export function isOwnShape(value: string): boolean {
  return OWN_PREFIXES.some((prefix) => value.startsWith(prefix));
}

/**
 * The URL value of a markup element: its `data-public-id`, else `fallbackId` (its DOM
 * id / option value). `null` when neither exists.
 */
export function urlValueOf(el: Element | null | undefined, fallbackId?: string | null): string | null {
  const publicValue = el?.getAttribute('data-public-id');
  if (publicValue) return publicValue;
  return fallbackId ?? null;
}

/** The URL value of a wire row (product-page JSON island): `urlId` when present, else `foreignId`. */
export function urlValueOfRow(row: { readonly foreignId: string; readonly urlId?: string | undefined }): string {
  return row.urlId ?? row.foreignId;
}

/** Whether `value` names a candidate exactly — any of its ids (URL value, DOM id, …) equals it. */
export function namesExactly(value: string, ids: readonly (string | null | undefined)[]): boolean {
  return ids.some((id) => id !== null && id !== undefined && id !== '' && id === value);
}
